import moment from 'moment';
import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Typography
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';

import { PrimaryTextField } from 'theme/CommonTheme';
import { normalizeDeliverToOtherParties } from 'components/TrustCenter/store/utils/agreementV2Normalization';
import { convertLocalDateTimeString } from 'components/Common/Utils/DateUtils';
import { TMS_OPTIONS } from 'components/TrustCenter/store/utils/routingKeysUtils';
import SharingRuleGroupsSection from './SharingRuleGroupsSection';
import css from './SharingAgreement.module.css';

const DIRECTION_OPTIONS = ['Inbound', 'Outbound', 'Bidirectional'];

/**
 * V2 sharing agreement detail: Accounts & Schedule, then sharing rule groups (messageTypes).
 * @param {{ agreement: import('components/TrustCenter/sharingAgreementV2.typedefs').V2SharingAgreement }} props
 */
const SharingAgreementV2DetailLayout = ({
    providerDisplayName,
    customerDisplayName,
    agreement,
    onChange,
    onMessageTypesChange,
    isReadOnly,
    dateError,
    eventTypeOptions = []
}) => {
    const isNewAgreement = !agreement.id;
    const showTmsSelector =
        isNewAgreement &&
        (agreement.direction === 'Inbound' || agreement.direction === 'Bidirectional');
    const deliverToOtherPartiesChecked = normalizeDeliverToOtherParties(
        agreement.deliverToOtherParties
    );
    const localTodayMin = moment().format('YYYY-MM-DD');

    return (
        <Box className={css.detailLayoutRoot}>
            <Paper elevation={0} className={css.detailSection}>
                <Typography variant="subtitle1" className={css.detailSectionTitle}>
                    Accounts & Schedule
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <PrimaryTextField
                            label="Provider Name"
                            id="providerNameDisplay"
                            value={providerDisplayName}
                            disabled
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <PrimaryTextField
                            label="Customer Name"
                            id="customerNameDisplay"
                            value={customerDisplayName}
                            disabled
                            fullWidth
                        />
                    </Grid>
                    {agreement.createdAt && (
                        <Grid item xs={12} md={6}>
                            <PrimaryTextField
                                label="Created At"
                                id="createdAt"
                                value={convertLocalDateTimeString(
                                    agreement.createdAt.replace('Z', '')
                                )}
                                disabled
                                fullWidth
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <PrimaryTextField
                            required
                            name="direction"
                            label="Direction"
                            id="direction"
                            value={agreement.direction || ''}
                            onChange={onChange}
                            select
                            disabled={isReadOnly}
                            fullWidth
                        >
                            {DIRECTION_OPTIONS.map((direction) => (
                                <MenuItem key={direction} value={direction}>
                                    {direction}
                                </MenuItem>
                            ))}
                        </PrimaryTextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <PrimaryTextField
                            required
                            name="validFrom"
                            label="Valid From"
                            id="validFrom"
                            type="date"
                            fullWidth
                            value={
                                agreement.validFrom
                                    ? moment(agreement.validFrom).format('YYYY-MM-DD')
                                    : ''
                            }
                            inputProps={{
                                min: localTodayMin
                            }}
                            InputProps={{
                                endAdornment: agreement.validFrom && !isReadOnly && (
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            onChange({ target: { name: 'validFrom', value: '' } })
                                        }
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                            onChange={onChange}
                            disabled={isReadOnly}
                            error={Boolean(dateError?.validFrom)}
                            helperText={dateError?.validFrom || ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <PrimaryTextField
                            name="validTo"
                            label="Valid To"
                            id="validTo"
                            type="date"
                            fullWidth
                            inputProps={{
                                min: localTodayMin
                            }}
                            value={
                                agreement.validTo
                                    ? moment(agreement.validTo).format('YYYY-MM-DD')
                                    : ''
                            }
                            InputProps={{
                                endAdornment: agreement.validTo && !isReadOnly && (
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            onChange({ target: { name: 'validTo', value: '' } })
                                        }
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                            onChange={onChange}
                            disabled={isReadOnly}
                            error={Boolean(dateError?.validTo)}
                            helperText={dateError?.validTo || ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} className={css.checkboxCell}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    id="deliverToOtherParties"
                                    name="deliverToOtherParties"
                                    checked={deliverToOtherPartiesChecked}
                                    onChange={onChange}
                                    disabled={isReadOnly}
                                />
                            }
                            label="Deliver to other parties"
                        />
                    </Grid>
                    {showTmsSelector && (
                        <Grid item xs={12} md={6}>
                            <PrimaryTextField
                                required
                                name="tms"
                                label="TMS"
                                id="tms"
                                data-testid="tms-selector"
                                value={agreement.tms || ''}
                                onChange={onChange}
                                select
                                disabled={isReadOnly}
                                fullWidth
                            >
                                {TMS_OPTIONS.map((tmsOption) => (
                                    <MenuItem key={tmsOption} value={tmsOption}>
                                        {tmsOption}
                                    </MenuItem>
                                ))}
                            </PrimaryTextField>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <Paper elevation={0} className={css.detailSection}>
                <SharingRuleGroupsSection
                    messageTypes={agreement.messageTypes}
                    onMessageTypesChange={onMessageTypesChange}
                    isReadOnly={isReadOnly}
                    eventTypeOptions={eventTypeOptions}
                />
            </Paper>
        </Box>
    );
};

export default SharingAgreementV2DetailLayout;
